import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAcceptMyInvitation, useMyPendingInvitations } from '../../../entities/invitation';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { Button } from '../../../shared/ui/button';
import { Notice } from '../../../shared/ui/notice';

/**
 * For someone who signed up on their own before following their invitation link: one notice per
 * live invitation addressed to their confirmed email, above every signed-in page. Accepting never merges
 * orgs; it adds a membership and goes there. On `/` it leaves that to the home page, which redirects into the new
 * org as soon as the refreshed scope shows it: a navigation of its own would land after that redirect and pull
 * the person back from the workspace to the org address.
 */
export function PendingInvitationsBanner() {
  const { t } = useTranslation('invite');
  const navigate = useNavigate();
  const pending = useMyPendingInvitations();
  const accept = useAcceptMyInvitation();
  const [error, setError] = useState<string | null>(null);
  const { pathname } = useLocation();
  if (!pending.data || pending.data.length === 0) return null;
  return (
    <div className="mx-auto grid w-full max-w-5xl gap-2 px-4 pt-6 sm:px-6">
      {pending.data.map((invitation) => (
        <Notice
          key={invitation.id}
          action={
            <Button
              size="sm"
              disabled={accept.isPending}
              onClick={async () => {
                setError(null);
                const homeRedirects = pathname === '/';
                try {
                  const joined = await accept.mutateAsync(invitation.id);
                  if (!homeRedirects) navigate(`/o/${joined.organization_slug}`);
                } catch (e) {
                  setError(identityErrorMessage(e));
                }
              }}
            >
              {t('banner.join', { org: invitation.organization_name })}
            </Button>
          }
        >
          {invitation.invited_by_name
            ? t('banner.invitedBy', {
                name: invitation.invited_by_name,
                org: invitation.organization_name,
              })
            : t('banner.invited', { org: invitation.organization_name })}
        </Notice>
      ))}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
